import {
  DEFAULT_HEADERS,
  CHAT_BASE_URL,
  AGENT_BASE_URL,
} from './config';

/**
 * Fetch order details when the chat page loads.
 * @param {string} sessionId - Unique session identifier.
 * @param {string} uuid - Unique identifier for the order context.
 * @returns {Promise<object>} The order details from the backend.
 */
export async function fetchOrderDetails(sessionId, uuid) {
  const response = await fetch(`${CHAT_BASE_URL}/${sessionId}/${uuid}`, {
    method: 'GET',
    headers: DEFAULT_HEADERS,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch the customer email/username for the session.
 * @param {string} sessionId - Unique session identifier.
 * @param {string} uuid - Unique identifier for the order context.
 * @returns {Promise<object>} The username data from the backend.
 */
export async function fetchUsername(sessionId, uuid) {
  const response = await fetch(`${CHAT_BASE_URL.replace('/chat', '/username')}/${sessionId}/${uuid}`, {
    method: 'GET',
    headers: DEFAULT_HEADERS,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Send a user message to the chat backend.
 * @param {string} sessionId - Unique session identifier.
 * @param {string} userMessage - The user's input text.
 * @param {string} customerEmail - The customer's email address.
 * @returns {Promise<string>} The parsed answer string from the backend.
 */
/**
 * Connect to a live agent for the current session.
 * @param {string} sessionId - Unique session identifier.
 * @param {string} uuid - Unique identifier for the order context.
 * @param {string} recentQuestion - The most recent user question.
 * @returns {Promise<object>} The response from the backend.
 */
export async function connectAgent(sessionId, uuid, recentQuestion) {
  const response = await fetch(`${AGENT_BASE_URL}/${sessionId}/${uuid}`, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({ recent_question: recentQuestion }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

const DEFAULT_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

// Reads the error text returned by /question; falls back to a generic message when absent or not JSON.
async function getBackendErrorMessage(response) {
  try {
    const body = await response.json();
    const message = [body?.message, body?.detail, body?.error].find(value => typeof value === 'string' && value.trim());
    return message ? message.trim() : DEFAULT_ERROR_MESSAGE;
  } catch {
    return DEFAULT_ERROR_MESSAGE;
  }
}

export async function sendMessage(sessionId, uuid, userMessage, customerEmail, sessionTimeout = 'false') {
  const response = await fetch(`${CHAT_BASE_URL.replace('/chat', '/question')}/${sessionId}/${uuid}`, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      dataframe_split: {
        columns: ['text', 'uid', 'conversion_id', 'customer_email', 'session_timeout'],
        data: [[userMessage, uuid, sessionId, customerEmail, sessionTimeout]],
      },
    }),
  });

  if (!response.ok) {
    const error = new Error(await getBackendErrorMessage(response));
    error.status = response.status;
    throw error;
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(DEFAULT_ERROR_MESSAGE);
  }

  // Backend ended the session: notify it with a session-timeout call and log the result.
  // The guard stops the notification call from triggering another notification.
  if (data?.session_ended === true && sessionTimeout !== 'true') {
    sendMessage(sessionId, uuid, '', customerEmail, 'true')
      .then((answer) => console.log('Session ended /question response:', answer))
      .catch((err) => console.error('Failed to notify session ended:', err));
  }

  return parseChatResponse(data);
}

/**
 * Save the end-of-session feedback.
 * @param {string} sessionId - Unique session identifier.
 * @param {string} uuid - Unique identifier for the order context.
 * @param {number} rating - Star rating from 1 to 5.
 * @param {string} feedback - Written feedback (may be empty for 4-5 stars).
 */
export async function submitFeedback(sessionId, uuid, rating, feedback) {
  const response = await fetch(CHAT_BASE_URL.replace('/chat', '/feedback'), {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({ sessionId, uuid, rating, feedback }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
}

export function parseDirectLineAiResponse(activity) {
  if (activity?.from?.id === 'user' || activity?.from?.role === 'user') return null;
  if (activity?.type === 'event'
    && ['streaming_event', 'streaming_events', 'streaming_progress'].includes(activity.name)) return null;
  const candidates = [activity?.value?.response, activity?.value,
    activity?.channelData?.response, activity?.channelData];
  if (typeof activity?.text === 'string') {
    try {
      candidates.push(JSON.parse(activity.text));
    } catch {
      // Plain text replies are handled by the normal message receiver.
    }
  }
  const data = candidates.find(candidate => candidate && typeof candidate === 'object'
    && !Array.isArray(candidate) && (typeof candidate.answer === 'string'
      || typeof candidate.reply_text === 'string'
      || typeof candidate.escalation_required === 'boolean'
      || Array.isArray(candidate.streaming_events)));
  if (!data) return null;
  return parseChatResponse({
    ...data,
    answer: data.answer ?? data.reply_text ?? (activity.type === 'message' ? activity.text : '') ?? '',
  });
}

export function parseChatResponse(data) {

  // Backend can request an escalation to a live agent alongside any response
  // shape (e.g. mid-conversation), independent of the connect_live_agent flow.
  let escalationRequired = data.escalation_required === true;

  // Handle session end before parsing nested answer
  if (data.session_end_message) {
    return { content: data.session_end_message, sessionEndMessage: data.session_end_message, escalationRequired };
  }

  // Handle live agent connection
  if (data.connect_live_agent) {
    if (Array.isArray(data.live_agent_messages) && data.live_agent_messages.length > 0) {
      return { liveAgentMessages: data.live_agent_messages, escalationRequired };
    }
    if (data.live_agent_message) {
      return { liveAgentMessages: [data.live_agent_message], escalationRequired };
    }
  }

  const isAgentMessage = !!data.is_agent_message;

  // The backend has shipped two response shapes:
  //  - Old: `answer` is a JSON string wrapping `{ predictions: [{...}] }`.
  //  - New: `answer` is the plain reply text, with intent/proactive_message/
  //    streaming_events etc. as top-level siblings of `answer`.
  // Try the old nested shape first; fall back to treating `data` itself as
  // the prediction when `answer` isn't parseable JSON.
  let prediction = null;
  if (typeof data.answer === 'string') {
    const trimmed = data.answer.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        prediction = parsed?.predictions?.[0] || parsed;
      } catch (e) {
        prediction = null;
      }
    }
  }

  if (!prediction) {
    prediction = {
      reply_text: data.answer || '',
      proactive_message: data.proactive_message,
      intent: data.intent,
      order_details: data.order_details,
      order_card: data.order_card,
      streaming_events: data.streaming_events,
    };
  }

  escalationRequired = escalationRequired || prediction.escalation_required === true;
  let result = prediction.reply_text || '';
  const proactive = prediction.proactive_message || data.proactive_message || '';
  const intent = prediction.intent || '';
  const tableOrderNumber = prediction.order_details?.order_number || '';

  // Only surface the phases the UI displays progress for; drop 'understanding'
  // (shown instead as an immediate "Analyzing your request..." indicator the
  // moment the request is sent), 'executing' (percentage ticks) and 'error'
  // (handled separately via the catch block).
  const DISPLAYABLE_PHASES = ['planning', 'node_executed', 'complete'];
  const responseEvents = prediction.streaming_events || data.streaming_events;
  const streamingEvents = Array.isArray(responseEvents)
    ? responseEvents.filter((event) => DISPLAYABLE_PHASES.includes(event?.phase))
    : [];

  if (prediction.order_card) {
    const orderCard = { ...prediction.order_card };
    if (intent && !orderCard.intent) {
      orderCard.intent = intent;
    }
    return { content: result, orderCard, proactiveMessage: proactive, intent, tableOrderNumber, isAgentMessage, streamingEvents, escalationRequired };
  }

  if (proactive) {
    result += `<br/><br/>${proactive}`;
  }

  if (tableOrderNumber) {
    return { content: result, tableOrderNumber, proactiveMessage: '', intent, isAgentMessage, streamingEvents, escalationRequired };
  }

  if (isAgentMessage) {
    return { content: result, isAgentMessage: true, streamingEvents, escalationRequired };
  }

  return { content: result, streamingEvents, escalationRequired };
}
